import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { ChevronRight, Calendar, MapPin, Car } from 'lucide-react';
import { useTranslation } from '../LanguageContext';

const WikiBrandPage = () => {
  const { t } = useTranslation();
  const { brandName } = useParams();
  const [brand, setBrand] = useState(null);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [brandRes, modelsRes] = await Promise.all([
          axios.get(`${API}/wiki/brands/${brandName}`),
          axios.get(`${API}/wiki/brands/${brandName}/models`)
        ]);
        setBrand(brandRes.data);
        setModels(modelsRes.data);
      } catch (error) {
        console.error('Error fetching brand data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [brandName]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold mb-4">{t('wikiPage.brandNotFound')}</h2>
          <Link to="/wiki" className="text-cyan-500 hover:underline">
            {t('wikiPage.backToWiki')}
          </Link>
        </div>
      </div>
    );
  }

  // Group models by body type
  const modelsByType = models.reduce((acc, model) => {
    const type = model.body_types[0] || 'Other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(model);
    return acc;
  }, {});

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
          <Link to="/wiki" className="hover:text-cyan-500 transition-colors">Wiki</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">{brand.name}</span>
        </nav>

        {/* Brand Header */}
        <div className="glass-card rounded-2xl p-8 mb-12">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
            <div className="w-24 h-24 rounded-2xl bg-white/5 flex items-center justify-center overflow-hidden">
              {brand.logo_url ? (
                <img 
                  src={brand.logo_url} 
                  alt={brand.name}
                  className="w-16 h-16 object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <Car className="w-12 h-12 text-gray-400" />
              )}
            </div>
            <div className="flex-1">
              <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
                {brand.name}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-gray-400 mb-4">
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-cyan-500" />
                  {brand.country}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-cyan-500" />
                  {t('wikiPage.founded')} {brand.founded}
                </span>
              </div>
              <p className="text-gray-400 max-w-3xl">{brand.description}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="glass-card rounded-xl p-6 text-center">
            <p className="text-3xl font-bold text-cyan-500 mb-1">{models.length}</p>
            <p className="text-sm text-gray-400">{t('wikiPage.modelsCount')}</p>
          </div>
          <div className="glass-card rounded-xl p-6 text-center">
            <p className="text-3xl font-bold text-cyan-500 mb-1">{2026 - brand.founded}</p>
            <p className="text-sm text-gray-400">{t('wikiPage.yearsActive')}</p>
          </div>
          <div className="glass-card rounded-xl p-6 text-center">
            <p className="text-3xl font-bold text-cyan-500 mb-1">{Object.keys(modelsByType).length}</p>
            <p className="text-sm text-gray-400">{t('wikiPage.bodyTypes')}</p>
          </div>
          <div className="glass-card rounded-xl p-6 text-center">
            <p className="text-3xl font-bold text-cyan-500 mb-1">{brand.country}</p>
            <p className="text-sm text-gray-400">{t('wikiPage.origin')}</p>
          </div>
        </div>

        {/* Models */}
        <h2 className="font-heading text-2xl font-semibold mb-6">{t('wikiPage.allModels')}</h2>
        
        {Object.entries(modelsByType).map(([type, typeModels]) => (
          <div key={type} className="mb-10">
            <h3 className="text-lg font-medium text-gray-400 mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                <Car className="w-4 h-4 text-cyan-500" />
              </div>
              {type}
              <span className="text-sm text-gray-600">({typeModels.length})</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {typeModels.map(model => (
                <Link
                  key={model.id}
                  to={`/wiki/model/${model.id}`}
                  className="glass-card rounded-xl overflow-hidden hover:border-cyan-500/50 transition-all group"
                  data-testid={`model-card-${model.id}`}
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img 
                      src={model.image_url} 
                      alt={model.name}
                      className="w-full h-full object-cover car-image-stylized group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <h4 className="font-heading text-xl font-semibold group-hover:text-cyan-500 transition-colors">
                        {model.name}
                      </h4>
                      <p className="text-sm text-gray-400">
                        {model.years[0]} - {model.years[model.years.length - 1] || 'Present'}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}

        {models.length === 0 && (
          <div className="text-center py-12">
            <Car className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">{t('wikiPage.noModels')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WikiBrandPage;
